import { api, type ApiResponse } from '@/lib/api';
import type {
  AddUnitUserRequest,
  CreateUnitRequest,
  ListResponse,
  UnitQuery,
  UnitResponse,
  UnitUserResponse,
  UpdateUnitRequest,
  UpdateUnitUserRequest,
} from '@troia-v3/types';

const BASE_URL = '/units';

/**
 * API client for the Units module.
 *
 * Mirrors backend endpoints (Task 4 — `TROIA-V3-BACKEND/src/modules/units`):
 * - GET    /api/units                    — list with pagination/filters
 * - GET    /api/units/:id                — get single unit
 * - POST   /api/units                    — create
 * - PUT    /api/units/:id                — update (partial)
 * - DELETE /api/units/:id                — delete
 * - GET    /api/units/:unitId/users      — list members
 * - POST   /api/units/:unitId/users      — add member
 * - PATCH  /api/units/:unitId/users/:userId — update member (role/priority/status)
 * - DELETE /api/units/:unitId/users/:userId — remove member
 *
 * `GET /units/dropdown` é consumido por `@/shared/hooks/useUnitsDropdown`
 * (cross-module) — não duplicado aqui.
 */
export const unitsApi = {
  async list(query?: UnitQuery): Promise<ListResponse<UnitResponse>> {
    const response = await api.get<ApiResponse<ListResponse<UnitResponse>>>(BASE_URL, { params: query });
    return response.data.data!;
  },

  async getById(id: string): Promise<UnitResponse> {
    const response = await api.get<ApiResponse<UnitResponse>>(`${BASE_URL}/${id}`);
    return response.data.data!;
  },

  async create(data: CreateUnitRequest): Promise<UnitResponse> {
    const response = await api.post<ApiResponse<UnitResponse>>(BASE_URL, data);
    return response.data.data!;
  },

  async update(id: string, data: UpdateUnitRequest): Promise<UnitResponse> {
    const response = await api.put<ApiResponse<UnitResponse>>(`${BASE_URL}/${id}`, data);
    return response.data.data!;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  // ── Membros (unit-users) ──────────────────────────────────────────────

  async listUsers(unitId: string): Promise<UnitUserResponse[]> {
    const response = await api.get<ApiResponse<UnitUserResponse[]>>(`${BASE_URL}/${unitId}/users`);
    return response.data.data!;
  },

  async addUser(unitId: string, data: AddUnitUserRequest): Promise<UnitUserResponse> {
    const response = await api.post<ApiResponse<UnitUserResponse>>(`${BASE_URL}/${unitId}/users`, data);
    return response.data.data!;
  },

  async updateUser(unitId: string, userId: string, data: UpdateUnitUserRequest): Promise<UnitUserResponse> {
    const response = await api.patch<ApiResponse<UnitUserResponse>>(`${BASE_URL}/${unitId}/users/${userId}`, data);
    return response.data.data!;
  },

  async removeUser(unitId: string, userId: string): Promise<void> {
    await api.delete(`${BASE_URL}/${unitId}/users/${userId}`);
  },
};
